import React from "react";
import {DashboardItemValue, PartnersChartDto} from "../../domain";
import {tawreedTranslationService} from "../../../../common/translations";
import {Chart} from "primereact/chart";
import {Skeleton} from "primereact/skeleton";
import {Card} from "primereact/card";
import {TawreedCalendar} from "../../../../common/components/form";
import {SelectButton} from "primereact/selectbutton";
import {PrimeIcons} from "primereact/api";
import {DateUtils} from "../../../../helpers/date";
import { AuthUtils } from "../../../auth/domain";


declare type DashboardPartnersChartProps = {

    className: string;

    /**
     *
     */
    loading: boolean;

    /**
     * dto
     */
    dto: PartnersChartDto | undefined;

    /**
     *
     * @param startDate
     * @param endDate
     */
    onSearch(startDate: Date, endDate: Date): void;

    /**
     *
     * @param value
     */
    calculator: (value: DashboardItemValue) => string;
}

declare type DashboardPartnersChartState = {

    selected: 'amount' | 'count',
    data: {
        /**
         *
         */
        labels: Array<string>;

        /**
         *
         */
        datasets: Array<{ label: string; backgroundColor: string; data: Array<any>; [key: string]: any }>;
    };
    //
    available: Array<{ label: string; value: 'amount' | 'count' }>;
    //
    startDate: Date;
    endDate: Date;
}

export const DashboardPartnersChart: React.FC<DashboardPartnersChartProps> = (props) => {


    const [state, setState] = React.useState<DashboardPartnersChartState>({
        selected: 'count',
        data: {
            labels: [],
            datasets: [
                {
                    label: tawreedTranslationService.translate('lbl_dashboard_partners_chart_total_count'),
                    backgroundColor: '#FFA726',
                    data: []
                }
            ]
        },
        //
        available: [
            {
                label: tawreedTranslationService.translate('lbl_dashboard_partners_chart_total_count'),
                value: 'count',
            },
            {
                label: tawreedTranslationService.translate('lbl_dashboard_partners_chart_total_amount'),
                value: 'amount',
            },
        ],
        //
        startDate: DateUtils.now(),
        endDate: DateUtils.now(),
    });

    React.useEffect(
        () => {
            let mounted = true;
            if (mounted && !props.loading) {
                props.onSearch(state.startDate, state.endDate);
            }

            return () => {
                mounted = false;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state.startDate, state.endDate]);

    React.useEffect(
        () => {

            setState(previous => {
                return {
                    ...previous,
                    data: {
                        labels: props.dto ? props.dto[previous.selected].value.map(e => tawreedTranslationService.translate(e.label)) : [],
                        datasets: [
                            {
                                label: tawreedTranslationService.translate('lbl_dashboard_partners_chart_total_' + previous.selected),
                                backgroundColor: props.dto ? props.dto[previous.selected].color : '#FFA726',
                                data: props.dto ? props.dto[previous.selected].value.map(e => props.calculator(e.value)) : [],
                                barThickness: 30,
                                maxBarThickness: 32,
                            }
                        ]
                    },
                }
            })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.dto, props.calculator, state.selected]);

    const options = {
        maintainAspectRatio: false,
        responsive: true,
        height: 300,
        plugins: {
            legend: {
                align: 'end',
                labels: {
                    font: {
                        family: AuthUtils.current().language === 'ar' ? '--arabic-font-family' : 'var(--font-family)'
                    },
                    color: '#495057'
                },
            }
        },
        scales: {
            x: {
                ticks: {
                    color: '#495057'
                },
                grid: {
                    color: '#ebedef'
                }
            },
            y: {
                ticks: {
                    color: '#495057'
                },
                grid: {
                    color: '#ebedef'
                }
            }
        }
    };

    const onChange = (sender: 'startDate' | 'endDate', value: Date) => {
        setState({...state, [sender]: value});
    }

    function onSelectedDataSetChange(e: 'amount' | 'count') {
        setState({...state, selected: e});
    }

    const header = () => {
        return (
            <div className="grid">
                <div className="col-12">
                    <div className="grid">
                        <div className="col-12 sm:col-6">
                            <SelectButton optionLabel="label" optionValue="value" value={state.selected} options={state.available} onChange={(e) => onSelectedDataSetChange(e.value)}/>
                        </div>
                        <div className="col-12 sm:col-6">
                            <div className="flex justify-content-start sm:justify-content-end">
                                <i className={"my-auto mr-2 " + PrimeIcons.CALENDAR} style={{fontSize: '1.5rem'}}/>
                                <div style={{width: '12rem'}}>
                                    <TawreedCalendar render="standalone" disabled={props.loading} value={state.startDate} onChange={e => onChange('startDate', e.value as Date)}/>
                                </div>
                                <div style={{width: '1rem', height: '1rem'}}/>
                                <div style={{width: '12rem'}}>
                                    <TawreedCalendar render="standalone" disabled={props.loading} value={state.endDate} onChange={e => onChange('endDate', e.value as Date)}/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <Card className={"card " + props.className} header={header()}>
            {
                (props.loading) ? <Skeleton/> : <Chart key="tawreed_dashboard_partners_chart" id="tawreed_dashboard_partners_chart" type="bar" height="16rem" data={state.data} options={options} style={{width: '100%'}}/>
            }
        </Card>
    )
}
